/**
 * Motion preferences — the single seam that tames screen-shake / hit-stop for
 * the K-3 target audience and honours the 「減少晃動」 (reduce-motion) toggle.
 *
 * WHY a dedicated module (not inline in JuiceLayer):
 * - Keeps JuiceLayer decoupled from learn-side settings / progress (no
 *   `src/game` → `src/learn` import). The battle scene reads the saved flag
 *   from `shared/progress` on create and pushes it in via `setReduceMotion`.
 * - Gives clamp constants + reduce-motion math one home so it is unit-testable
 *   without spinning up a Phaser scene.
 *
 * 鐵則: purely presentational. Nothing here reads or writes 戰力 / progress /
 * damage / HP / cooldown — it only scales a camera-shake intensity number.
 */

/**
 * K-3 hard ceiling on camera-shake intensity (Phaser `cameras.main.shake`
 * intensity is a 0–1 fraction of viewport). Adult action games happily push
 * 0.02–0.05; for 8–9 year-olds the GAME_DESIGN_RESEARCH note flags that as a
 * motion-sickness risk, so we cap every shake at this value regardless of what
 * the call site requested. The loudest existing call site asks for 0.02
 * (boss death) — clamping to 0.012 keeps a clear "big hit" punch while shaving
 * the most nauseating amplitude. Smaller hits (0.005–0.008) are untouched.
 */
export const MAX_SHAKE_INTENSITY = 0.012;

/**
 * When reduce-motion is ON, multiply the (already-clamped) intensity by this
 * factor. 0.15 ≈ a faint rumble that still confirms "you hit something" without
 * moving the whole screen. Not 0 on purpose: a tiny residual keeps hit-feedback
 * legible for kids who rely on it, while being gentle enough for the sensitive.
 */
export const REDUCED_SHAKE_FACTOR = 0.15;

/**
 * When reduce-motion is ON, scale hit-stop (freeze-frame) duration by this
 * factor. Hit-stop itself does not move the camera, but a long timeScale=0
 * freeze paired with damped shake feels janky, so we shorten it too. Floored
 * at 0 — see `reducedHitStopMs`.
 */
export const REDUCED_HITSTOP_FACTOR = 0.5;

/**
 * Module-level cached flag. Defaults to false (off) so behaviour is unchanged
 * until the battle scene explicitly pushes the saved preference in. Cached
 * (rather than read per-shake) because shake fires every few frames and we do
 * not want a localStorage / progress read in that hot path.
 */
let _reduceMotion = false;

/** Push the saved 減少晃動 preference into the game runtime. Idempotent. */
export function setReduceMotion(flag: boolean): void {
  _reduceMotion = !!flag;
}

/** Current cached reduce-motion flag (game-side). */
export function isReduceMotion(): boolean {
  return _reduceMotion;
}

/**
 * Resolve a requested camera-shake intensity into the value actually used,
 * applying (1) the K-3 hard clamp and (2) the reduce-motion damping.
 *
 * @param requested  Intensity the call site asked for (0–1).
 * @returns          Clamped + possibly damped intensity, never NaN, never < 0,
 *                   never above MAX_SHAKE_INTENSITY.
 */
export function clampShakeIntensity(requested: number): number {
  // Guard garbage (NaN / negative) → no shake.
  if (!Number.isFinite(requested) || requested <= 0) return 0;
  // 1. K-3 hard ceiling.
  let v = Math.min(requested, MAX_SHAKE_INTENSITY);
  // 2. Reduce-motion damping.
  if (_reduceMotion) v *= REDUCED_SHAKE_FACTOR;
  return v;
}

/**
 * Resolve a requested hit-stop duration (ms) into the value actually used.
 * Identity when reduce-motion is off; scaled down (and floored at 0) when on.
 *
 * @param requestedMs  Freeze duration the call site asked for.
 */
export function reducedHitStopMs(requestedMs: number): number {
  if (!Number.isFinite(requestedMs) || requestedMs <= 0) return 0;
  if (!_reduceMotion) return requestedMs;
  return Math.max(0, requestedMs * REDUCED_HITSTOP_FACTOR);
}
